// Get status colour - matches getSubtaskProgress in Dashboard.js/ClientView.js
export const getStatusColour = (status, isClosed, isMeeting = false) => {
    if (isMeeting) return '#e8d78a'; // pale yellow for meetings
    if (isClosed) return '#64748b'; // slate-500 - darker
    // Convert to string to handle numeric status IDs from DynamoDB
    const statusLower = String(status || '').toLowerCase();
    if (statusLower.includes('ready') || statusLower.includes('done')) return '#5eada4'; // muted teal
    if (statusLower.includes('review') || statusLower.includes('test')) return '#9775c4'; // muted purple
    if (statusLower.includes('progress') || statusLower.includes('doing')) return '#5b9bd5'; // muted blue
    if (statusLower.includes('needs info')) return '#dc7c7c'; // subtle red - action required
    return '#a1a1aa'; // zinc-400 - neutral grey for New
};

// The single definition of the scale maths - column width and the date-to-pixel
// conversion every position in both Gantts is derived from.
//
// monthDays differs by chart and is NOT an inconsistency to tidy away: the
// project Gantt uses 30 (calendar-ish months), while TemplateGantt labels its
// month columns W1-W4 (see f727334) so a month there IS four weeks. Feeding it 30
// would put the geometry out of step with the headers it draws.
export const getColumnMetrics = (scale, zoomLevel = 1, monthDays = 30) => {
    const baseWidth = scale === 'day' ? 30 : scale === 'week' ? 60 : 100;
    const daysPerCol = scale === 'day' ? 1 : scale === 'week' ? 7 : monthDays;
    const colWidth = baseWidth * zoomLevel;
    return { baseWidth, daysPerCol, colWidth, pxPerDay: colWidth / daysPerCol };
};

// ===========================================================================
// The SECOND scale, and the gesture arithmetic (T364/T365/T369, 2026-08-30)
// ===========================================================================
//
// ⛔ THERE ARE TWO SCALES ON THIS CHART AND THEY OWN DIFFERENT AXES. NOTHING
// MULTIPLIES THEM TOGETHER.
//
//   zoomLevel   the TIME AXIS - colWidth, pxPerDay. Slider, -/+, FIT, ALL,
//               a horizontal pinch, shift+wheel.
//   uiScale     READING SIZE - row height, bar height, TYPE. No width at all.
//               A vertical pinch, ctrl+wheel, the V.Zoom reset.
//
// getColumnMetrics above takes zoomLevel ALONE. organise-app shipped the
// multiplied form and Bill reversed it after using it ("I need this to zoom
// vertically only rather than zoom"), then had to correct it a second time
// because uiScale was still widening the left panel - a panel growing sideways
// IS horizontal movement. Do not reintroduce an `effectiveZoom`.
// ⛔ 0.3, NOT 0.6 (Bill, 2026-08-30: "I need it to go down to 30%"). The floor
// is the whole point of the second scale - a 200-row plan has to fit on one
// screen as SHAPE. What makes 30% legible is that the text comes off below
// UI_SCALE_TEXT_MIN rather than shrinking into noise; the two changed together
// and neither stands alone.
export const UI_SCALE_MIN = 0.3;
export const UI_SCALE_MAX = 2.5;
export const UI_SCALE_DEFAULT = 1;
export const UI_SCALE_STEP = 0.05;    // ctrl+wheel, additive
export const ZOOM_WHEEL_STEP = 0.10;  // shift+wheel, additive
// ⛔ 0.2 matches the SLIDER (ZoomSlider.js:11), not the -/+ buttons' 0.25.
// Bill's ruling, 2026-08-30, taken over "match the buttons" and over "make
// everything 0.25" - the last would have changed the slider's 20/30/40 steps.
// Consequence he accepted in the same breath: at 20% the - button still jumps
// to 25%, i.e. zooms IN. That is true on main today and is not a new defect.
export const ZOOM_WHEEL_MIN = 0.2;
export const ZOOM_WHEEL_MAX = 8;
export const PINCH_AXIS_GATE = 24;    // px of START separation, per axis
export const TOUCH_GRIP = 44;
export const MOUSE_GRIP = 12;
// ⛔ A touch release that never travelled past this is a TAP, not a drag - and
// the bar drag must COMMIT NOTHING, not just skip the card. The commit path
// rounds deltaX into days, and at a zoomed-out scale a day is a few pixels, so
// an ungated release lets a finger wobble silently move a task 1-3 days.
//
// ⛔ 24, AND IT REPLACES BILL'S OWN 8 (T405, 2026-08-30, after iPad testing:
// "I was just testing it and accidently moved one of the objects dates", then
// "maybe making a little more difficult"). Do not read the change as a
// regression against the 2026-08-29 ruling - it is that ruling revised by the
// man who made it, on the device it was made for.
//
// ⛔ AND ONE CONSTANT DOES BOTH JOBS HERE. Both charts read it for the COMMIT
// gate, and the ladder's card logic reads it as TAP_SLOP - so this single edit
// moves both rungs together, which is what was asked for. organise-app needed
// two numbers for the same behaviour; do not split this into two to match it.
//
// ⚠️ THE DEAD ZONE IS NOW 5px TO 24px, AND ITS OUTCOME IS THE HOVER CARD. The
// hold is already cancelled at 5px, so a finger that slips this far earns
// neither gesture and the release falls through to the tap. That is the
// harmless answer for a slip.
//
// ⚠️ AND IT IS PIXELS, SO IT IS WORTH MORE DAYS AT A COARSE SCALE: about seven
// days at month scale, under one at day scale. That was equally true of 8, and
// it is why the CONFIRMATION rather than this number is what stops a wrong date
// being written (Planning/GANTT-DRAG-CONFIRM-2026-08-30.md §5).
export const TOUCH_TAP_SLOP = 24;

// ⚠️ 2.5, 0.05, 24 and 44 came from organise-app, where they were never
// signed off on a device - they are carried unchanged so the two apps behave
// alike, and they are on the plan's device-walk list. 0.10 IS Bill's ("Make it
// 10%", "5% is tool small for horizontal"), as is the 5% vertical step. The
// floor was 0.6 from the same port until 2026-08-30, when Bill took it to 0.3.

const roundTo = (value, step) => Number((Math.round(value / step) * step).toFixed(4));
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// ⛔ NOT a Rule-4 fallback. This is the strict parse of a persisted UI value -
// the same shape GanttState.js already uses for its boolean keys and for the
// numeric zoom migration. A stored value that is not a number is not a missing
// input to paper over; there is no such thing as "no reading size".
//
// ⛔ AND IT DOES NOT SNAP. The wheel accumulates on a raw value and only the
// APPLIED value is snapped - snapping here would round a trackpad's 0.15%
// events straight back to where they started and the chart would never move.
export const clampUiScale = (value) =>
    Number.isFinite(value) ? clamp(value, UI_SCALE_MIN, UI_SCALE_MAX) : UI_SCALE_DEFAULT;

// Clamp BEFORE rounding and again after: rounding an in-range value can carry
// it past a bound, and clamping only once leaves whichever end that was.
export const snapUiScale = (value) =>
    clamp(roundTo(clamp(value, UI_SCALE_MIN, UI_SCALE_MAX), UI_SCALE_STEP),
        UI_SCALE_MIN, UI_SCALE_MAX);

// ⛔ AT AND BELOW THIS, EVERY LABEL COMES OFF THE CHART (Bill, 2026-08-30:
// "50% and below the text needs to hide as it won't be readable"). At 0.5 the
// bar type is 9px and the panel type 7.5px. Inclusive: 50% hides, 55% shows.
//
// The horizontal axis has had the same rule since long before this - the day
// column labels vanish at `zoomLevel <= 0.5` (GanttChart.js:3170). This is its
// vertical twin, deliberately the same shape and the same number.
export const UI_SCALE_TEXT_MIN = 0.5;

// ⛔ JUDGED ON THE SNAPPED VALUE, WHICH IS WHAT THE READOUT SHOWS. The V.Zoom
// button renders Math.round(snapUiScale(uiScale) * 100), and ALL writes an
// EXACT fit that is not on the 0.05 grid. Judge the raw value and a uiScale of
// 0.51 reads "50%" on screen with the text still showing - the readout and the
// chart contradicting each other over a rounding step nobody can see.
export const ganttTextVisible = (uiScale) => snapUiScale(uiScale) > UI_SCALE_TEXT_MIN;

// ⛔ ALL NEVER ZOOMS THE TYPE UP (Bill, 2026-08-30: "If zooming in lock the
// V.Zoom at 100% it can't go above the text is huge"). A four-row template in a
// tall window fits at 250%, and ALL would blow the type up to fill the screen.
// UI_SCALE_MAX (2.5) is untouched - the pinch and ctrl+wheel still reach it.
export const UI_SCALE_FIT_MAX = UI_SCALE_DEFAULT;

// The vertical half of ALL: the reading size at which rowCount rows fill
// availableHeight, clamped to [UI_SCALE_MIN, UI_SCALE_FIT_MAX].
//
// ⛔ RETURNS null WHEN IT CANNOT MEASURE, and null means "leave uiScale alone".
// It is not a Rule-4 fallback: an unmeasured container has no fit, and there is
// no sensible reading size to invent for one.
//
// ⛔ FLOORS TO A WHOLE PIXEL FIRST. rowHeight is Math.round(base * scale)
// (GanttChart.js:353), so a scale taken straight from the division can round a
// row UP and push the last row under the fold - a fit that does not fit.
// Flooring the row height and dividing back makes that Math.round exact.
export const fitUiScale = (availableHeight, rowCount, baseRowHeight) => {
    if (!(availableHeight > 0) || !(rowCount > 0) || !(baseRowHeight > 0)) return null;
    const rowPx = Math.floor(availableHeight / rowCount);
    if (rowPx <= 0) return UI_SCALE_MIN;
    return clamp(rowPx / baseRowHeight, UI_SCALE_MIN, UI_SCALE_FIT_MAX);
};

// ⛔ THE PERCENTAGE IS NOT THE ONLY THING IN THE BAR, AND PADDING NEVER SHRINKS
// (Fable, F1, 2026-08-30 - the first draft of this constant was 16 and let the
// label spill about 10px past the edge at boundary widths, which is the defect
// it exists to remove). The name span beside it is `flex-1 min-w-0 truncate`,
// so its TEXT shrinks to nothing - but its `pl-3 pr-1` does not, and 16px of it
// is still in the flex row. Add the label's own pr-2.5 and the floor is 26.
//
//   pl-3 (12) + pr-1 (4) on the name span, GanttChart.js:3965  = 16
//   pr-2.5 (10) on the percentage span itself, GanttChart.js:3979 = 10
export const PERCENT_LABEL_PAD = 26;

// The blocked warning icon is a flex sibling too (GanttChart.js:3952-3961):
// FaExclamationTriangle at 12px inside a wrapper carrying `ml-1`. It renders
// only when `hasIssueBlocker && showText`, which is exactly when this check
// runs, so the caller passes it in rather than it being assumed either way.
export const BLOCKED_ICON_WIDTH = 16;

// ⛔ reservedPx IS MANDATORY, NOT DEFAULTED. What else is in the bar differs per
// call site and a default of 0 would silently under-reserve at whichever site
// forgot it - the exact shape of the bug being fixed. Pass 0 explicitly.
//
// ⛔ A LABEL WIDTH OF 0 MEANS NOT MEASURED, NOT "ZERO WIDE". makeTextMeasurer
// returns a constant-0 measurer where there is no 2d context (nameFlow.js:17),
// so treating 0 as a real width would hide every percentage in that
// environment. Unmeasured therefore SHOWS - exactly today's behaviour - and the
// component tests stub getContext (GanttChart.test.js:37) so they exercise the
// real rule rather than this escape hatch.
export const percentLabelFits = (boxWidth, labelWidth, reservedPx) =>
    !(labelWidth > 0) || boxWidth >= labelWidth + PERCENT_LABEL_PAD + reservedPx;

// The name span's own pl-3 pr-1 (GanttChart.js:3965), which survives min-w-0
// exactly as PERCENT_LABEL_PAD's does.
export const BAR_NAME_PAD = 16;

// Does the bar's own name have to be cut? reservedPx is whatever ELSE is in the
// bar's flex row - the percentage plus its pr-2.5, and the blocked icon - and is
// mandatory for the same reason percentLabelFits's is.
//
// ⛔ UNMEASURED (0) MEANS "AS TODAY", which here is the opposite way round from
// percentLabelFits and deliberately so: today there is no after-bar label at
// all, so a measurer that cannot measure produces none. Today a percentage
// always shows, so an unmeasured one still shows. Both say "behave as today";
// the two answers differ because the two defaults do.
export const barNameOverflows = (barWidth, nameWidth, reservedPx) =>
    nameWidth > 0 && nameWidth > barWidth - BAR_NAME_PAD - reservedPx;

// The after-bar name, ported VERBATIM from organise-app's GanttRow.js - the
// classes at its line 376 and the glow at 385 (Bill, 2026-08-30: "Same style so
// check the Organise code"). A restyle there is a decision here too.
export const AFTER_BAR_NAME_CLASSES =
    'whitespace-nowrap text-[calc(14px*var(--gantt-scale))] font-medium italic text-gray-700';

// Three white layers plus a small hard dark drop, so the glyphs stay clean where
// a dependency line passes behind them and still lift off the pale rows the glow
// blends into.
export const AFTER_BAR_NAME_GLOW =
    '1px 1px 2px rgba(0, 0, 0, 0.2), 0 0 2px rgba(255, 255, 255, 1), 0 0 4px rgba(255, 255, 255, 0.95), 0 0 6px rgba(255, 255, 255, 0.8)';

// ⛔ CLEAR OF THE RESIZE GRIP. organise draws its label at
// `left + width + gripWidth + 8` because the italic slant's first letter
// otherwise lands on the grip; TME's grip is 8px (GanttChart.js:3895), so the
// same arithmetic is the bar's end + 16.
export const AFTER_BAR_NAME_OFFSET = 16;

// A subtask row's workflow status as a NAME. The row builder writes
// `statusName || status` into `status` and the raw id into `statusId`
// (GanttRowBuilder.js:200, :332), so the name is usually already there and the
// numeric case is the one that needs the catalogue.
//
// ⛔ RETURNS null RATHER THAN A PLACEHOLDER, and the caller omits the line. A
// status that resolves to nothing is missing data, and "Status: Unknown" or
// "Status: 3" is exactly the fallback the house rules forbid - it would read as
// a real state on a card people make decisions from.
//
// ⛔ AND IT GATES ON row.type, BECAUSE `status` MEANS TWO DIFFERENT THINGS. On a
// `task` row it is the GROUP's name - the epic or workflow status the main task
// sits under (GanttRowBuilder.js:171, :303). Ungated, a main task's hover card
// would print `Status: Migration Prep`: an epic's name presented as a workflow
// status, with nothing failing.
export const statusLabel = (row, taskStatuses) => {
    if (row.type !== 'subtask') return null;
    if (typeof row.status === 'string' && row.status.trim()) return row.status;
    const found = (taskStatuses || []).find(s => String(s.id) === String(row.statusId));
    return found?.name || null;
};

// ⛔ THE WHEEL'S ALONE. This must never reach useGanttState: FIT and ALL write
// an EXACT fit, and snapping that would mis-fit the chart on the two buttons
// whose entire job is to fit it.
export const snapZoom = (value) =>
    clamp(roundTo(clamp(value, ZOOM_WHEEL_MIN, ZOOM_WHEEL_MAX), ZOOM_WHEEL_STEP),
        ZOOM_WHEEL_MIN, ZOOM_WHEEL_MAX);

// ⛔ An outward notch may never return a value LARGER than the one it started
// from, nor an inward notch a smaller one.
//
// With the current numbers both wheel ranges happen to sit on their own grids,
// so nothing can round the wrong way - but that is an accident of the values,
// not a property of the code. Someone moving a bound or a step should not have
// to re-derive whether the new pair is on-grid to know the chart still zooms
// the way the user pushed it. It also covers the case where the chart is
// already outside a bound: from a slider-set 0.2 with a floor of 0.25, an
// outward notch refuses to move rather than dragging the chart UP to the floor.
export const directionSafe = (current, next, outward) =>
    outward ? Math.min(current, next) : Math.max(current, next);

// Keep a point fixed while the scale changes.
//
// `offset` is the pointer's distance into the scrolled axis, or 0 to anchor the
// leading edge. ⛔ THE TWO AXES PASS DIFFERENT THINGS, AND THE ASYMMETRY IS THE
// DESIGN:
//
//   horizontal   anchors the POINTER. The time axis is dense with content
//                wherever you point, so the day under the cursor stays put.
//                ⚠️ Measured from the TIMELINE's left edge, not the
//                container's - the left panel is sticky and does not scroll.
//   vertical     anchors the TOP EDGE, offset 0, so this is just scrollTop *
//                ratio. Bill, in organise: "when using ctrl + wheel the
//                projects don't stay at the top, so it zooms to an empty
//                chart." Keeping whichever row is at the top edge where it is
//                is what "keep my place in a list" means, and a list is what
//                this axis is.
export const anchoredScaleScroll = (scroll, offset, ratio) =>
    Math.max(0, (scroll + offset) * ratio - offset);

// ⛔ 44px for a finger, 12px for a mouse or a pen, answered PER EVENT.
//
// Never a media query and never a breakpoint: an iPad with a trackpad gets 12px
// from the trackpad and 44px from a finger in the same session, and only
// pointerType can say which is which. Bill chose the pen as precise.
//
// The width/3 cap still binds, or a 60px bar would be all grip and could never
// be moved at all. The PAINTED handle stays on the mouse's figure - a finger
// gets a bigger hit zone than the handle it can see, which is the point.
export const gripFor = (pointerType, barWidth) =>
    Math.min(pointerType === 'touch' ? TOUCH_GRIP : MOUSE_GRIP, barWidth / 3);

// Which end of a bar a press landed on, or 'move' for the middle.
//
// ⛔ THIS IS THE FUNCTION THE CSS-SCALE DECISION WAS MEASURED ON. Under a CSS
// `zoom` or `transform: scale`, getBoundingClientRect and clientX are SCALED
// while clientWidth/scrollLeft are not - and a press dispatched at a bar's
// exact centre came back as `resize-right`, i.e. moving a task would have
// resized it. That is why this chart scales by multiplying its own numbers and
// never by a CSS transform.
export const dragTypeFor = (clickX, barWidth, pointerType) => {
    const grip = gripFor(pointerType, barWidth);
    if (clickX <= grip) return 'resize-left';
    if (clickX >= barWidth - grip) return 'resize-right';
    return 'move';
};

// Parent/summary bar colour (like SVAR green)
export const PARENT_COLOUR = '#10b981'; // emerald-500 - Migration Experts tasks
export const CLIENT_PARENT_COLOUR = '#6b9bc4'; // muted blue - Client/Team tasks
export const STATUS_COLOUR = '#7c6b9e'; // muted purple for status groups
export const PROJECT_COLOUR = '#2f5f96'; // navy blue for project bar - lightened
                                         // so the darker progress wash reads
                                         // against it (see getProgressWash)
export const KICKOFF_COLOUR = '#6b21a8'; // dark purple for kick-off line

// Create gradient from lighter left to darker right
export const getBarGradient = (baseColour) => {
    const lighterVersions = {
        '#2f5f96': '#5b8fc9', // project - navy
        '#7c6b9e': '#a090c0', // status - purple
        '#10b981': '#22c58a', // parent - green (darker for readability)
        '#6b9bc4': '#8bb5d8', // client parent - muted blue
        '#64748b': '#94a3b8', // closed - slate
        '#5b9bd5': '#8bbef0', // in progress - blue
        '#9775c4': '#b99de0', // review - purple
        '#5eada4': '#7bc4bc', // ready - muted teal
        '#a1a1aa': '#b0b0b8', // new - grey (darker for readability)
        '#dc7c7c': '#e8a0a0', // needs info - subtle red
        '#e8d78a': '#f5ebb8', // meeting - pale yellow
    };
    const lighter = lighterVersions[baseColour] || baseColour;
    // Left-to-right since 2026-08-23, ported from organise-app (Bill, there:
    // "slight gradient... left to right, subtle"). A weekend-gapped bar calls
    // this once PER SEGMENT, so the ramp restarts in each one and a gapped bar
    // reads as bands - accepted 2026-08-23, not an oversight.
    return `linear-gradient(to right, ${lighter} 0%, ${baseColour} 100%)`;
};

// Black wash laid over the completed portion of a bar.
//
// The project bar gets a heavier one. It is the widest bar on the chart and its
// progress is the number people actually read, but at the shared 0.15 the
// completed portion was indistinguishable from the remainder.
export const getProgressWash = (rowType) =>
    rowType === 'project' ? 'rgba(0,0,0,0.42)' : 'rgba(0,0,0,0.15)';

// Helper to extract numeric ref from task ID (handles "ST#0001" -> 1 or "1" -> 1)
// Only MT#/ST# prefixes and plain numbers are valid. Legacy ADMT#/ADST# are rejected.
export const getNumericRef = (id) => {
    if (id == null) return NaN;
    if (typeof id === 'number') return id;
    const str = String(id);
    if (str.startsWith('ST#') || str.startsWith('MT#')) {
        return parseInt(str.replace(/^[SM]T#/, ''), 10);
    }
    // Reject legacy ADMT#/ADST# formats explicitly
    if (str.startsWith('ADMT#') || str.startsWith('ADST#')) return NaN;
    return parseInt(str, 10);
};

// Dependency line colours
export const DEPENDENCY_COLOURS = {
    gray: '#6b7280',
    red: '#ef4444',
    blue: '#3b82f6',
    green: '#22c55e',
    orange: '#f97316',
    purple: '#8b5cf6',
    pink: '#ec4899',
    yellow: '#eab308',
    cyan: '#06b6d4',
    indigo: '#6366f1',
    teal: '#14b8a6',
    rose: '#f43f5e'
};
export const COLOUR_OPTIONS = [
    { name: 'gray', label: 'Gray' },
    { name: 'red', label: 'Red' },
    { name: 'blue', label: 'Blue' },
    { name: 'green', label: 'Green' },
    { name: 'orange', label: 'Orange' },
    { name: 'purple', label: 'Purple' },
    { name: 'pink', label: 'Pink' },
    { name: 'yellow', label: 'Yellow' },
    { name: 'cyan', label: 'Cyan' },
    { name: 'indigo', label: 'Indigo' },
    { name: 'teal', label: 'Teal' },
    { name: 'rose', label: 'Rose' }
];

// Calculate progress percentage based on status position in workflow
// New = 0%, Closed = 100%, Needs Info = 0%, others = based on position
export const calculateStatusProgress = (subtask, taskStatuses) => {
    if (subtask.isClosed) return 100;

    const statusName = String(subtask.statusName || '').toLowerCase();
    if (statusName === 'new' || statusName.includes('needs info')) return 0;

    // Get working statuses (exclude New, Closed, Needs Info)
    const workingStatuses = taskStatuses
        .filter(s => {
            const name = (s.name || '').toLowerCase();
            return !s.isClosed && name !== 'new' && !name.includes('needs info');
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (workingStatuses.length === 0) return 0;

    // Find current status position
    const currentStatus = taskStatuses.find(s =>
        String(s.id) === String(subtask.status) || s.name === subtask.statusName
    );
    if (!currentStatus) return 0;

    const positionIndex = workingStatuses.findIndex(s => s.id === currentStatus.id);
    if (positionIndex === -1) return 0;

    // Calculate progress: (position + 1) / (total + 1) * 100
    // This leaves room for 0% (New) and 100% (Closed)
    return Math.round(((positionIndex + 1) / (workingStatuses.length + 1)) * 100);
};
